<template>
  <!-- Info icon explaining the G1–G11 signal groups. Opens on hover and can be
       pinned open with a click (closes again on click or click-outside). -->
  <div
    ref="root"
    class="relative inline-flex"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <button
      type="button"
      class="inline-flex h-5 w-5 items-center justify-center rounded-full border-0 bg-white/10 p-0 text-white/60 transition-colors hover:bg-white/20 hover:text-white/90"
      :class="pinned ? 'bg-white/20 text-white/90' : ''"
      title="Signal groups (G1–G11)"
      @click.stop="pinned = !pinned"
    >
      <i class="el-icon-info text-[12px]"></i>
    </button>

    <div
      v-if="open"
      class="absolute top-6 z-50 max-h-[60vh] w-80 max-w-[90vw] overflow-y-auto rounded-sm border border-white/10 bg-[#1b2128] p-3 text-left shadow-xl"
      :class="align === 'left' ? 'left-0' : 'right-0'"
      @click.stop
    >
      <div class="mb-2 flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wide text-white/80">Signal groups</span>
        <span class="text-[10px] text-white/40">G1–G11</span>
      </div>
      <ul class="space-y-1.5">
        <li
          v-for="g in groups"
          :key="g.id"
          class="flex gap-2"
        >
          <span class="mt-0.5 h-fit shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-white/75">{{ g.id }}</span>
          <span class="min-w-0">
            <span class="text-xs font-semibold text-white/90">{{ g.name }}</span>
            <span
              class="ml-1 rounded px-1 text-[9px] uppercase leading-none tracking-wide"
              :class="classBadge(g.class)"
            >{{ g.class }}</span>
            <span class="block text-xs leading-snug text-white/55">{{ g.indicates }}</span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onBeforeUnmount,
} from 'vue';
import { signalGroupList } from '../utils/signalGroups';

// Which edge of the icon the popover is anchored to. 'right' (default) grows
// leftward — correct when the icon sits at the right of its row; 'left' grows
// rightward — use when the icon is near the left edge so it isn't clipped.
const props = defineProps({
  align: {
    type: String,
    default: 'right',
  },
});
const align = computed(() => (props.align === 'left' ? 'left' : 'right'));

const groups = signalGroupList();

const root = ref(null);
const hovered = ref(false);
const pinned = ref(false);
const open = computed(() => hovered.value || pinned.value);

// Class → badge colour, mirroring the severity ceiling of each group:
// impact = red, stress = amber, context = sky.
function classBadge(cls) {
  if (cls === 'impact') return 'bg-red-500/20 text-red-200';
  if (cls === 'context') return 'bg-sky-500/20 text-sky-200';
  return 'bg-amber-500/20 text-amber-200';
}

// Clicking anywhere outside the popover un-pins it.
function onDocClick(event) {
  if (pinned.value && root.value && !root.value.contains(event.target)) {
    pinned.value = false;
  }
}
onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>
